/**
 * IndexedDB через Dexie
 * Версионирование схемы для миграций
 */
import Dexie, { type Table } from 'dexie'

export interface EventRecord {
  id: string // локальный UUID
  employee_id: string
  site_id: string
  device_id?: string
  event_type: 'check_in' | 'check_out'
  source: 'scan' | 'manual'
  happened_at: string // ISO string
  payload_hash: string
  qr_token?: string
  synced: number // 0 = false, 1 = true (для Dexie индексов)
  sync_attempts: number
  sync_failed: number // 0 = false, 1 = true (для Dexie индексов)
  created_at: string // ISO string
}

export interface LogRecord {
  id: string
  level: 'info' | 'warn' | 'error'
  message: string
  timestamp: string // ISO string
  metadata?: Record<string, unknown>
}

export interface ConfigRecord {
  key: string
  value: string | number | boolean | object
  updated_at: string // ISO string
}

export interface EmployeeCacheRecord {
  employee_id: string
  external_id: string
  full_name: string
  phone_number?: string
  role: string
  public_key: string
  key_version: number
  is_active: boolean
  short_id?: number // UUID → uint16/uint32 маппинг для компактных токенов
  totp_secret?: string // TOTP secret (base32) для TOTP+HMAC токенов
  // Права доступа администратора (если role === 'admin')
  can_access_admin_panel?: boolean
  can_access_tablet?: boolean
  is_developer?: boolean
  cached_at: string // ISO string
}

export interface AdminPermissionsCache {
  employee_id: string // Ключ для кеша (employee_id, для которого сгенерирован код)
  can_access_admin_panel: boolean
  can_access_tablet: boolean
  is_developer: boolean
  allowed_site_ids: string[]
  organization_id?: string
  cached_at: string // ISO string
  expires_at: string // ISO string (код действует 5 минут)
}

class RecvisitDB extends Dexie {
  events!: Table<EventRecord>
  logs!: Table<LogRecord>
  config!: Table<ConfigRecord>
  employees_cache!: Table<EmployeeCacheRecord>
  admin_permissions_cache!: Table<AdminPermissionsCache>

  constructor() {
    super('RecvisitDB')
    
    // Версия 1: начальная схема
    // Примечание: Dexie не поддерживает boolean индексы напрямую, используем числа (0/1)
    this.version(1).stores({
      events: 'id, employee_id, site_id, synced, happened_at, payload_hash',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id',
    })
    
    // Версия 2: добавляем индекс sync_failed для запросов по failed событиям
    this.version(2).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id',
    }).upgrade(async (tx) => {
      // Миграция: устанавливаем sync_failed: 0 для всех существующих записей
      const events = await tx.table('events').toCollection().toArray()
      for (const event of events) {
        if (event.sync_failed === undefined) {
          await tx.table('events').update(event.id, { sync_failed: 0 })
        }
      }
    })
    
    // Версия 3: добавляем составной индекс [employee_id+site_id+happened_at]
    this.version(3).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id',
    })
    
    // Версия 4: расширяем схему employees_cache для хранения полных данных сотрудников
    this.version(4).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active, short_id',
    }).upgrade(async (tx) => {
      // Миграция: добавляем новые поля для существующих записей
      const employees = await tx.table('employees_cache').toCollection().toArray()
      for (const employee of employees) {
        // Если нет новых полей, устанавливаем дефолтные значения
        if (!employee.full_name) {
          await tx.table('employees_cache').update(employee.employee_id, {
            external_id: employee.employee_id, // Используем employee_id как external_id
            full_name: `Сотрудник ${employee.employee_id.substring(0, 8)}`,
            role: 'worker',
            is_active: true,
          })
        }
      }
    })
    
    // Версия 5: добавляем таблицу admin_permissions_cache для кеширования прав администратора
    this.version(5).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active',
      admin_permissions_cache: 'employee_id, expires_at',
    })
    
    // Версия 6: добавляем поле short_id для бинарных токенов (UUID → uint32 маппинг)
    this.version(6).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active, short_id',
      admin_permissions_cache: 'employee_id, expires_at',
    }).upgrade(async (tx) => {
      // Миграция: вычисляем short_id для существующих сотрудников
      const employees = await tx.table('employees_cache').toCollection().toArray()
      for (const employee of employees) {
        if (employee.short_id === undefined) {
          try {
            // Импортируем EmployeeIdMapper из shared/binaryToken
            // В контексте миграции используем простой расчёт
            const hex = employee.employee_id.replace(/-/g, '').substring(0, 8)
            const shortId = parseInt(hex, 16)
            await tx.table('employees_cache').update(employee.employee_id, {
              short_id: shortId
            })
          } catch (error) {
            console.error(`Failed to calculate short_id for ${employee.employee_id}:`, error)
          }
        }
      }
    })

    // Версия 7: ИСПРАВЛЕНИЕ - убираем short_id из индексов, т.к. Dexie не может индексировать undefined
    // Данные short_id и totp_secret будут загружены из backend при следующей синхронизации
    this.version(7).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active',  // УБРАЛИ short_id из индексов!
      admin_permissions_cache: 'employee_id, expires_at',
    }).upgrade(async (tx) => {
      // КРИТИЧНО: версия 6 вычисляла неправильные short_id из UUID
      // Очищаем кеш полностью - данные загрузятся заново из backend
      console.log('[DB Migration v7] Clearing employee cache (will reload from backend)...')
      await tx.table('employees_cache').clear()
      
      // КРИТИЧНО: Очищаем ETag из localStorage, иначе backend вернёт 304 Not Modified
      // и SyncQueue загрузит ПУСТОЙ кеш, вызвав "Сотрудник не найден"
      localStorage.removeItem('syncqueue_etag')
      console.log('[DB Migration v7] ETag cache cleared to force full sync')
      console.log('[DB Migration v7] Employee cache cleared, awaiting sync...')
    })
    
    // Версия 8: Добавляем индекс short_id ПОСЛЕ того, как данные загружены из backend
    this.version(8).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active, short_id',  // Теперь short_id будет индексироваться
      admin_permissions_cache: 'employee_id, expires_at',
    })
    
    // Версия 9: КРИТИЧНОЕ ИСПРАВЛЕНИЕ - проверка ETag cache poisoning
    // Для устройств, которые уже мигрировали на v7/v8 с пустым кешем и stale ETag
    this.version(9).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active, short_id',
      admin_permissions_cache: 'employee_id, expires_at',
    }).upgrade(async (tx) => {
      const employeeCount = await tx.table('employees_cache').count()
      const hasETag = !!localStorage.getItem('syncqueue_etag')
      if (employeeCount === 0 && hasETag) {
        localStorage.removeItem('syncqueue_etag')
        console.log('[DB Migration v9] ETag cache poisoning detected and fixed!')
      }
    })

    // Версия 10: Обслуживание базы данных (дефрагментация SQLite).
    // Chrome хранит IndexedDB в SQLite. После 1+ недели работы со старым кодом
    // таблица logs накапливала >80 000 записей/день (диагностический спам),
    // что приводило к сильной фрагментации SQLite и замедлению ВСЕХ IDB-запросов.
    // Замедление вызывало O(N)-скан в getLastEvent → >5s → OPERATION_TIMEOUT → "Ошибка обработки!"
    // Очистка логов безопасна — это диагностика, не бизнес-данные.
    //
    // ВАЖНО: всё тело миграции в try/catch.
    // Если что-то пойдёт не так (неожиданная структура данных, IDB-ошибка),
    // migration всё равно "успешно" завершится, и DB откроется на версии 10.
    // Очистка — оптимизация производительности, не критическая операция.
    this.version(10).stores({
      events: 'id, employee_id, site_id, synced, sync_failed, happened_at, payload_hash, [employee_id+site_id+happened_at]',
      logs: 'id, level, timestamp',
      config: 'key',
      employees_cache: 'employee_id, full_name, is_active, short_id',
      admin_permissions_cache: 'employee_id, expires_at',
    }).upgrade(async (tx) => {
      try {
        console.log('[DB Migration v10] Database maintenance starting...')
        
        // Шаг 1: очищаем таблицу logs (дефрагментация SQLite)
        try {
          await tx.table('logs').clear()
          console.log('[DB Migration v10] Logs table cleared')
        } catch (logErr) {
          // Логи — диагностика, если не удалось очистить — не критично
          console.warn('[DB Migration v10] Could not clear logs (non-critical):', logErr)
        }
        
        // Шаг 2: удаляем старые синхронизированные события (>7 дней)
        try {
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
          const oldSynced = await tx.table('events')
            .where('synced').equals(1)
            .filter((e: { happened_at: string }) => e.happened_at < sevenDaysAgo)
            .toArray()
          if (oldSynced.length > 0) {
            await tx.table('events').bulkDelete(oldSynced.map((e: { id: string }) => e.id))
            console.log(`[DB Migration v10] Removed ${oldSynced.length} old synced events`)
          }
        } catch (evtErr) {
          // События уже на сервере — если не удалось очистить локально, не критично
          console.warn('[DB Migration v10] Could not clean old events (non-critical):', evtErr)
        }
        
        console.log('[DB Migration v10] Database maintenance complete')
      } catch (outerErr) {
        // Внешний catch: migration никогда не должна бросать исключение.
        // Если бросит — Dexie откатит транзакцию и будет пытаться снова при каждом старте.
        console.error('[DB Migration v10] Unexpected error (ignored to prevent retry loop):', outerErr)
      }
    })
  }
}

export const db = new RecvisitDB()

// Инициализация конфигурации
export async function initConfig(): Promise<void> {
  const schemaVersion = await db.config.get('schema_version')
  const currentVersion = 10 // Текущая версия схемы (дефрагментация SQLite)
  
  if (!schemaVersion) {
    // Первая инициализация
    await db.config.add({
      key: 'schema_version',
      value: currentVersion,
      updated_at: new Date().toISOString(),
    })
  } else if (schemaVersion.value !== currentVersion) {
    // Обновление версии схемы после миграции
    await db.config.update('schema_version', {
      value: currentVersion,
      updated_at: new Date().toISOString(),
    })
  }
}

